import type { Header } from '@tanstack/react-table'
import { flexRender, type Row, type Table as TanstackTable } from '@tanstack/react-table'
import type * as React from 'react'

import { cn } from '../../lib/utils'
import { DataTableActions, type DataTableRowAction } from './data-table-actions'
import { useDataTableArrival } from './data-table-arrival'
import { DataTablePhoneRowDetails } from './data-table-phone-detail-line'
import {
  getPhoneActionSlot,
  getPhoneDetailIndexes,
  getPhoneRoles,
  PHONE_TABLE_CLASSES,
  phoneCellClass,
  phoneRowClass,
} from './data-table-phone-layout'
import './data-table-column-meta'
import { handleRowKeyDown } from './data-table-row-keys'
import { selectionColumn } from './data-table-selection-column'
import { SkeletonTable } from './skeleton'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from './table'

/**
 * What a feature may put on a row: drag handlers, `aria-grabbed`, data
 * attributes. `children` is the grid's own cells, so it is not accepted.
 */
type DataTableRowProps = Omit<React.HTMLAttributes<HTMLTableRowElement>, 'children'> & {
  [key: `data-${string}`]: string | undefined
}

/**
 * A dense grid over a caller-owned TanStack table instance.
 *
 * The instance is a prop, not state created here, because sorting, filtering,
 * pagination and selection all belong to the feature that knows what the rows
 * mean. @multaqa/ui carries no domain knowledge (AGENTS.md §4), and a grid
 * that owned its own query would have to.
 *
 * Column alignment is driven by `meta.numeric` rather than a className on every
 * cell: a numeric column's head and figures end-align with the logical
 * property, declared once on the column def. Figures are tabular because the
 * body sets it once; nothing here resets it. A negative figure keeps its sign
 * and takes `--danger` through `Figure` ("The Sign-and-Colour Rule").
 */
function DataTable<TData>({
  table,
  loading = false,
  empty,
  skeletonColumns,
  onRowActivate,
  className,
  tableClassName,
  'data-testid': dataTestId,
  caption,
  footer,
  rowProps,
  headProps,
  cellAlign = 'middle',
  stickyHeader = false,
  phoneLayout = 'table',
}: {
  table: TanstackTable<TData>
  loading?: boolean
  empty?: React.ReactNode
  skeletonColumns?: number[]
  onRowActivate?: (row: Row<TData>) => void
  // Styles the grid's own root wrapper — the element ABOVE the scroll
  // container `Table` renders around every `<table>`. Anything that must
  // stay inside the scroll port, most of all a minimum width, does not
  // belong here: put on this wrapper, it makes the wrapper itself the widest
  // thing on the page and the scroll container inside it never has anything
  // to scroll. Use `tableClassName` for that.
  className?: string
  // Styles the `<table>` element, inside `Table`'s scroll container. This is
  // the only prop that lands inside the scroll port, so a minimum width goes
  // here — `tableClassName="min-w-[1040px]"` — not on `className`. Putting it
  // on `className` instead defeats the scroll container: the wrapper grows to
  // the minimum width, the inner `overflow-x-auto` div is `w-full` of a parent
  // that is never narrower than that width, and the table always fits with
  // nothing to scroll. The overflow lands on the page instead of the table.
  tableClassName?: string
  // Applied to the grid's root wrapper, alongside `className`, so a screen
  // that already selects its rows and cells by testid has one for the grid
  // itself without wrapping `DataTable` in a caller-owned div — which would
  // put the id on a different element than existing callers query for.
  'data-testid'?: string
  // The table's accessible name. Kept sr-only by the grid itself — a visible
  // caption is a screen's decision, not the grid's.
  caption?: React.ReactNode
  footer?: React.ReactNode
  stickyHeader?: boolean
  phoneLayout?: 'table' | 'rows'
  // Attributes for each row, in both layouts. The grid's `data-state` wins over a
  // caller's; a caller's click and key handlers run before the grid's row
  // activation, not instead of it.
  rowProps?: (row: Row<TData>) => DataTableRowProps
  // Only className, aria-sort and aria-label are exposed; key and style belong to the grid.
  headProps?: (header: Header<TData, unknown>) => {
    className?: string
    'aria-sort'?: 'ascending' | 'descending' | 'none'
    'aria-label'?: string
  }
  // Vertical alignment for every body cell in the grid — not a column `meta`
  // field. Alignment is a whole-row decision: a row where one column wraps
  // and centres while its neighbour top-aligns is the defect this prop
  // exists to fix, and a per-column knob would let a caller recreate it one
  // column at a time. `TableCell` already carries `align-middle`, so
  // `'middle'` emits nothing; `'top'` emits `align-top` for a row that holds
  // a column of wrapped, multi-line text.
  cellAlign?: 'middle' | 'top'
}) {
  const headerGroups = table.getHeaderGroups()
  const rows = table.getRowModel().rows
  const phoneRows = phoneLayout === 'rows',
    arrivalRef = useDataTableArrival(loading, rows)
  const visibleColumns = phoneRows ? table.getVisibleLeafColumns() : []
  const phoneRoles = phoneRows ? getPhoneRoles(visibleColumns) : undefined
  const phoneDetailIndexes =
    phoneRows && phoneRoles ? getPhoneDetailIndexes(visibleColumns, phoneRoles) : undefined
  const phoneHasSelection = visibleColumns.some((column) => column.id === 'select')
  const phoneActionSlot = getPhoneActionSlot(visibleColumns, phoneRoles)
  const phoneHasActions = phoneActionSlot !== undefined
  const hasSelectionColumn = table.getVisibleLeafColumns().some((column) => column.id === 'select')

  if (loading) {
    return (
      <SkeletonTable
        columns={skeletonColumns ?? headerGroups[0]?.headers.map(() => 1) ?? [1]}
        headers={headerGroups[0]?.headers.map((header) =>
          typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : '',
        )}
      />
    )
  }

  return (
    <div className={cn('w-full', phoneRows && '@container', className)} data-testid={dataTestId}>
      <Table
        className={cn(tableClassName, phoneRows && PHONE_TABLE_CLASSES.table)}
        stickyHeader={stickyHeader}
      >
        {caption ? <TableCaption className="sr-only">{caption}</TableCaption> : null}
        <TableHeader className={phoneRows ? PHONE_TABLE_CLASSES.header : undefined}>
          {headerGroups.map((headerGroup) => (
            <TableRow key={headerGroup.id} className="hover:bg-transparent">
              {headerGroup.headers.map((header) => {
                const { className: headClassName, ...headAria } = headProps?.(header) ?? {}
                return (
                  <TableHead
                    key={header.id}
                    scope={header.colSpan > 1 ? 'colgroup' : 'col'}
                    style={{ width: phoneRows ? undefined : header.column.columnDef.meta?.width }}
                    {...headAria}
                    numeric={Boolean(header.column.columnDef.meta?.numeric)}
                    className={cn(
                      header.column.columnDef.meta?.actions && 'text-end',
                      header.column.columnDef.meta?.sticky &&
                        'sticky start-0 z-20 bg-card border-e border-border',
                      // Appended last, mirroring rowProps below, so a
                      // caller-supplied class can add to but never crowd out
                      // the grid's own alignment and sticky classes.
                      headClassName,
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody ref={arrivalRef} className={phoneRows ? PHONE_TABLE_CLASSES.body : undefined}>
          {rows.map((row) => {
            const {
              className: rowClassName,
              onClick,
              onKeyDown,
              ...rowAttributes
            } = rowProps?.(row) ?? {}
            return (
              <TableRow
                key={row.id}
                {...rowAttributes}
                data-row-id={row.id}
                data-state={row.getIsSelected() ? 'selected' : undefined}
                // A row is activated by click OR by Enter when focused. Click
                // alone makes the grid unusable from the keyboard, which on a
                // screen whose primary interaction is keyboard navigation is not
                // a small omission.
                tabIndex={onRowActivate ? 0 : rowAttributes.tabIndex}
                onClick={
                  onRowActivate
                    ? (event) => {
                        onClick?.(event)
                        onRowActivate(row)
                      }
                    : onClick
                }
                onKeyDown={
                  onRowActivate
                    ? (event) => {
                        onKeyDown?.(event)
                        handleRowKeyDown(event, () => onRowActivate(row), hasSelectionColumn)
                      }
                    : onKeyDown
                }
                className={cn(
                  onRowActivate && 'cursor-pointer',
                  phoneRows && phoneRowClass(phoneHasSelection, phoneHasActions),
                  // A selected row is grey ink plus its ticked checkbox, with
                  // no edge marker (DESIGN.md "Data tables"); `TableRow`
                  // draws it from `data-state`. The signature layer soaks it
                  // in from the same attribute.
                  //
                  // Appended last so a caller-supplied tint (e.g. an inactive
                  // row) can add to, but never silently lose, the grid's own
                  // activation class above.
                  rowClassName,
                )}
              >
                {row
                  .getVisibleCells()
                  .filter((cell) => phoneRoles?.get(cell.column.id) !== 'actions')
                  .map((cell) => {
                    const role = phoneRoles?.get(cell.column.id)
                    const isSelectionColumn = cell.column.id === 'select'
                    const isSelection = phoneRows && isSelectionColumn
                    return (
                      <TableCell
                        key={cell.id}
                        numeric={Boolean(cell.column.columnDef.meta?.numeric)}
                        data-phone-role={role}
                        data-phone-selection={isSelection || undefined}
                        className={cn(
                          cell.column.columnDef.meta?.actions && 'text-end',
                          cell.column.columnDef.meta?.sticky &&
                            'sticky start-0 z-10 border-e border-border bg-card [tr:hover>&]:bg-accent [tr[data-state=selected]>&]:bg-brand-subtle',
                          cellAlign === 'top' && 'align-top',
                          phoneRows &&
                            phoneCellClass(role, {
                              selection: isSelection,
                              actions: cell.column.id === phoneActionSlot,
                              hasSelection: phoneHasSelection,
                            }),
                        )}
                        // A click on the checkbox ticks the row; it must not also
                        // activate it. The actions cell is the same case.
                        onClick={
                          cell.column.columnDef.meta?.actions || isSelectionColumn
                            ? (event) => event.stopPropagation()
                            : undefined
                        }
                      >
                        {cell.column.columnDef.meta?.actions ? (
                          <DataTableActions
                            actions={cell.column.columnDef.meta.actions(row)}
                            label={cell.column.columnDef.meta.actionsLabel?.(row)}
                            phoneRows={phoneRows}
                          />
                        ) : (
                          flexRender(cell.column.columnDef.cell, cell.getContext())
                        )}
                      </TableCell>
                    )
                  })}
                {phoneRows && phoneRoles && phoneDetailIndexes ? (
                  <DataTablePhoneRowDetails
                    cells={row.getVisibleCells()}
                    row={row}
                    roles={phoneRoles}
                    detailIndexes={phoneDetailIndexes}
                    hasSelection={phoneHasSelection}
                    hasActions={phoneHasActions}
                  />
                ) : null}
              </TableRow>
            )
          })}
        </TableBody>
        {rows.length > 0 && footer ? (
          <TableFooter className="sticky bottom-0 bg-card">{footer}</TableFooter>
        ) : null}
      </Table>
      {rows.length === 0 && empty ? <div className="border-b border-border">{empty}</div> : null}
    </div>
  )
}

export { DataTable, type DataTableRowAction, type DataTableRowProps, selectionColumn }
